import { isMeetingSessionRoleGreaterThanOrEqual, MeetingProvider } from "@kalyzee/kast-web-sdk";
import { ROLE } from "@kalyzee/kast-web-sdk/http";
import { Loading, useURL } from "@kalyzee/kast-web-sdk/web-components";
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Route } from "react-router-dom";
import LoginRedirectionRoute from "../components/navigation/LoginRedirection";
import { NavigateSlave } from "../components/navigation/navigateSlave";
import { generateRoute } from "../components/navigation/Page";
import { PAGE_PATHS, PageMasterMode, PageSlaveMode } from "../components/navigation/PageContext";
import PrivateRoute from "../components/navigation/PrivateRoute";
import PublicRedirectionRoute from "../components/navigation/PublicRedirection";
import PublicRoute from "../components/navigation/PublicRoute";
import MasterRouteContainer from "../components/navigation/routes/MasterRouteContainer";
import SlaveRouteContainer from "../components/navigation/routes/SlaveRouteContainer";
import ConnexionView from "../components/utils/ConnexionView";
import { useLogger } from "../helpers/logger";
import { appMessage } from "../helpers/message";
import { getGlobalQuery, getGlobalQueryLabel, NavigateGlobalQuery } from "../helpers/navigate";
import { isRoleGreaterThanOrEqual } from "../helpers/roles";
import { sdkClient } from "../helpers/sdk";
import { loadSettings, Settings } from "../helpers/settings";
import { setToken, TokenType } from "../helpers/storage";
import { useAppDispatch } from "../hooks/app";
import { useDeviceControls } from "../hooks/deviceControls";
import { meetingsContext } from "../hooks/meeting";
import { useSettings } from "../hooks/settings";
import { AuthStatus } from "../interfaces/session";
import { ExpertContextProvider } from "../pages/master/expert/context";
import { MeetingListExpertPage } from "../pages/master/expert/list/list";
import MeetingCallExpertPage from "../pages/master/expert/meeting/call";
import { MeetingDashboardExpertPage } from "../pages/master/expert/meeting/dashboard";
import { MeetingGroupExpertPage } from "../pages/master/expert/meeting/group";
import { MeetingExpertPage } from "../pages/master/expert/meeting/meeting";
import MeetingSurveyExpertPage from "../pages/master/expert/meeting/survey";
import { VodExpertPage } from "../pages/master/expert/vod/vod";
import { PedagogyPageContainer } from "../pages/master/pedagogy/container";
import { PedagogyContextProvider } from "../pages/master/pedagogy/context";
import { DashboardPedagogyPage } from "../pages/master/pedagogy/dashboard";
import { MeetingPedagogyInterventionDashboardPage } from "../pages/master/pedagogy/meeting/intervention/content/dashboard";
import { MeetingPedagogyInterventionExpertPage } from "../pages/master/pedagogy/meeting/intervention/content/expert";
import { MeetingPedagogyInterventionGroupPage } from "../pages/master/pedagogy/meeting/intervention/content/group";
import { MeetingPedagogyInterventionHighlightPage } from "../pages/master/pedagogy/meeting/intervention/content/highlight";
import { MeetingPedagogyInterventionPage } from "../pages/master/pedagogy/meeting/intervention/intervention";
import { MeetingPedagogyInterventionRedirection } from "../pages/master/pedagogy/meeting/intervention/redirection";
import { MeetingPedagogyPage } from "../pages/master/pedagogy/meeting/meeting";
import { OverviewPedagogyPage } from "../pages/master/pedagogy/overview";
import { MeetingWaitingRoomPage } from "../pages/master/pedagogy/waitingRoom";
import { StudentContextProvider } from "../pages/master/student/context";
import { MeetingListStudentPage } from "../pages/master/student/list/list";
import { MeetingDashboardStudentPage } from "../pages/master/student/meeting/dashboard";
import { MeetingHighlightStudentPage } from "../pages/master/student/meeting/highlight";
import { MeetingStudentPage } from "../pages/master/student/meeting/meeting";
import { MeetingPedagogyStudentPage } from "../pages/master/student/meeting/pedagogy";
import ForbiddenPage from "../pages/public/forbidden/forbidden";
import LoginPage from "../pages/public/login/login";
import LogoutPage from "../pages/public/logout/logout";
import MeetingSlavePage from "../pages/slave/meeting/meeting";
import { useUserSession } from "../store/session/hooks";
import { SessionMode, setDisableGoogleLogin, setMicrophone, setMode, setScreenIndex, setScreenName, setWrapperAppData } from "../store/session/slices";
import "./App.css";
import { MeetingDashboardPedagogyPage } from "../pages/master/pedagogy/meeting/dashboard/dashboard";
import { MeetingExpertsWithPedagogyPage } from "../pages/master/expert/meeting/withPedagogy";
import { MeetingPedagogyInterventionSurveyPage } from "../pages/master/pedagogy/meeting/intervention/content/survey";
import { MeetingConnector } from "../pages/master/common/private/meetingConnector";
import { MeetingConnectorRenderer } from "../pages/master/common/private/meetingConnectorRenderer";
import { MeetingGroupStudentPage } from "../pages/master/student/meeting/group";
import { MediaContextProvider } from "../pages/master/common/private/mediaContext";
import MeetingSurveyStudentPage from "../pages/master/student/meeting/survey";

function App() {
  const url = useURL();
  const dispatch = useAppDispatch();
  const { user, mode, authStatus, userFetchStatus, retry } = useUserSession();
  const [settings, saveSettings] = useSettings();
  const logger = useLogger();

  useDeviceControls();

  useEffect(() => {
    if (getGlobalQuery(url.href, NavigateGlobalQuery.SLAVE) && mode !== SessionMode.SLAVE) {
      dispatch(setMode(SessionMode.SLAVE));
    } else if (!mode) dispatch(setMode(SessionMode.MASTER));
    const screenIndex = getGlobalQuery(url.href, NavigateGlobalQuery.SCREEN);
    const screenName = getGlobalQuery(url.href, NavigateGlobalQuery.SCREEN_NAME);
    if (screenIndex !== undefined) dispatch(setScreenIndex(screenIndex));
    if (screenName !== undefined) dispatch(setScreenName(screenName));
    const disableGoogleLogin = getGlobalQuery(url.href, NavigateGlobalQuery.DISABLE_GOOGLE_LOGIN);
    if (disableGoogleLogin !== undefined) dispatch(setDisableGoogleLogin(disableGoogleLogin));
    const token = getGlobalQuery(url.href, NavigateGlobalQuery.TOKEN);
    const refreshToken = getGlobalQuery(url.href, NavigateGlobalQuery.REFRESH_TOKEN);
    if (token) {
      setToken(token, TokenType.Normal);
      setToken(refreshToken, TokenType.Refresh);
      url.searchParams.delete(getGlobalQueryLabel(NavigateGlobalQuery.TOKEN));
      url.searchParams.delete(getGlobalQueryLabel(NavigateGlobalQuery.REFRESH_TOKEN));
      window.location.href = url.href;
    }
  }, [dispatch, url, mode]);

  useEffect(() => {
    const microphoneListener = (mute: boolean) => dispatch(setMicrophone({ mute }));
    const appDataListener = (data: { canClose?: boolean}) => dispatch(setWrapperAppData({ canClose: data?.canClose }));
    appMessage.addEventListener("microphone", microphoneListener);
    appMessage.addEventListener("appData", appDataListener);
    return () => {
      appMessage.removeEventListener("microphone", microphoneListener);
      appMessage.removeEventListener("appData", appDataListener);
    };
  }, [dispatch]);

  useEffect(() => {
    appMessage.getMicrohoneData();
    appMessage.getAppData();
    navigator.mediaDevices?.enumerateDevices().then((data) => logger.log("devices : ", data));
    return () => {
      appMessage.close();
    };
  }, []);

  useEffect(() => {
    appMessage.ready(settings.secret?.remotePassword ?? undefined);
  }, [settings.secret?.remotePassword]);

  useEffect(() => {
    const getListener = () => appMessage.postSettings(loadSettings());
    const setListener = (data: Partial<Settings>) => {
      const newSettings = saveSettings(data);
      logger.log("localstorageSetSettings : ", newSettings);
      appMessage.postSettings(newSettings);
    };
    appMessage.addEventListener("localstorageGetSettings", getListener);
    appMessage.addEventListener("localstorageSetSettings", setListener);
    return () => {
      appMessage.removeEventListener("localstorageGetSettings", getListener);
      appMessage.removeEventListener("localstorageSetSettings", setListener);
    };
  });

  useEffect(() => {
    if (settings.debug?.sdkClient?.public) (window as any).sdkClient = sdkClient;
  }, [settings]);

  const provideMeetingContext = (children: React.ReactNode) => (
    <MeetingProvider
      context={meetingsContext}
      sdkClient={sdkClient}
      useChatApdapter
      userId={user?.id}
      incomingStreamSessionValidator={(meeting, mySession, incomingSession) => {
        return incomingSession.id !== mySession.id;
      }}
      logs={typeof settings.logs === "boolean" ? settings.logs : settings.logs?.meetingContext}
      options={{
        receptionManager: {
          ovenMediaSession: {
            options: {
              retryAfter: 500,
              video: { mode: "subscription", delayToDisableStream: 5000 },
              audio: true,
            },
          },
          logger: (typeof settings.logs === "boolean" ? settings.logs : settings.logs?.ovenMediaSession?.reception?.main) ? logger.log : undefined,
          webrtcLogger: (typeof settings.logs === "boolean" ? settings.logs : settings.logs?.ovenMediaSession?.reception?.webrtc) ? logger.log : undefined,
        },
        emissionManager: {
          sdp: {
            formatter: {
              offer: settings?.emission?.sdpFormatter?.offer,
              answer: settings?.emission?.sdpFormatter?.answer,
            },
          },
          logger: (typeof settings.logs === "boolean" ? settings.logs : settings.logs?.ovenMediaSession?.emission?.main) ? logger.log : undefined,
          webrtcLogger: (typeof settings.logs === "boolean" ? settings.logs : settings.logs?.ovenMediaSession?.emission?.webrtc) ? logger.log : undefined,
        },
      }}
    >
      {children}
    </MeetingProvider>
  );

  const provideStudentContext = (children: React.ReactNode) => <StudentContextProvider>{children}</StudentContextProvider>;
  const provideExpertContext = (children: React.ReactNode) => <ExpertContextProvider>{children}</ExpertContextProvider>;
  const provideMediaContext = (children: React.ReactNode) => <MediaContextProvider>{children}</MediaContextProvider>;
  
  const provideMixedContexts = (children: React.ReactNode) => provideStudentContext(provideExpertContext(children));
  
  const providePedagogyContext = (children: React.ReactNode) => <PedagogyContextProvider>{children}</PedagogyContextProvider>;

  const provideMeetingConnector = (children: React.ReactNode) => <MeetingConnector>{children}</MeetingConnector>

  const privateRoutes = () => {
    if (!mode) return <Loading fullscreen />;

    if (mode === SessionMode.SLAVE) {
      return (
        <SlaveRouteContainer element={<PrivateRoute mode={mode} authStatus={authStatus} />}>
          {generateRoute(SessionMode.SLAVE, PageSlaveMode.MEETING, <MeetingSlavePage />)}
          <Route path="" element={<NavigateSlave to={PAGE_PATHS[mode][PageSlaveMode.MEETING]} />} />
          <Route path="*" element={<NavigateSlave to={PAGE_PATHS[mode][PageSlaveMode.MEETING]} />} />
        </SlaveRouteContainer>
      );
    }

    if (!user) return <Loading fullscreen />;
    const isPedagogy = isRoleGreaterThanOrEqual(user.role, ROLE.ADMIN);
    if (isPedagogy) {
      return (
        <MasterRouteContainer mode="pedagogy" element={provideMediaContext(providePedagogyContext(<PrivateRoute mode={mode} authStatus={authStatus} />))}>
          {generateRoute(SessionMode.MASTER, PageMasterMode.LOGOUT, <LogoutPage />)}
          {generateRoute(SessionMode.MASTER, PageMasterMode.LOGIN, <LoginRedirectionRoute defaultTo={PAGE_PATHS[mode][PageMasterMode.LIST]} />)}
          {generateRoute(SessionMode.MASTER, PageMasterMode.OVERVIEW, provideMeetingContext(<OverviewPedagogyPage />))},
          <Route path="/" element={provideMeetingContext(<PedagogyPageContainer />)}>
            {generateRoute(SessionMode.MASTER, PageMasterMode.DASHBOARD, <DashboardPedagogyPage />)},
            {generateRoute(SessionMode.MASTER, PageMasterMode.WAITING_ROOM, <MeetingWaitingRoomPage />)}
            {generateRoute(SessionMode.MASTER, PageMasterMode.MEETING, <MeetingPedagogyPage />, {
              children: (
                <>
                  {generateRoute(SessionMode.MASTER, PageMasterMode.MEETING_DASHBOARD, <MeetingDashboardPedagogyPage />)}
                  {generateRoute(SessionMode.MASTER, PageMasterMode.MEETING_INTERVENTION, <MeetingPedagogyInterventionPage />, {
                    children: (
                      <>
                        {generateRoute(SessionMode.MASTER, PageMasterMode.MEETING_INTERVENTION_TALK_TO_EXPERT, <MeetingPedagogyInterventionExpertPage />)}
                        {generateRoute(SessionMode.MASTER, PageMasterMode.MEETING_INTERVENTION_DASHBOARD, <MeetingPedagogyInterventionDashboardPage />)}
                        {generateRoute(SessionMode.MASTER, PageMasterMode.MEETING_INTERVENTION_HIGHLIGHT, <MeetingPedagogyInterventionHighlightPage />)}
                        {generateRoute(SessionMode.MASTER, PageMasterMode.MEETING_INTERVENTION_GROUP, <MeetingPedagogyInterventionGroupPage />)}
                        {generateRoute(SessionMode.MASTER, PageMasterMode.MEETING_INTERVENTION_SURVEY, <MeetingPedagogyInterventionSurveyPage />)}
                        <Route path="" element={<MeetingPedagogyInterventionRedirection />} />
                        <Route path="*" element={<MeetingPedagogyInterventionRedirection />} />
                      </>
                    ),
                  })}
                </>
              ),
            })}
            <Route path="" element={<Navigate to={PAGE_PATHS[mode][PageMasterMode.DASHBOARD]} />} />
            <Route path="*" element={<Navigate to={PAGE_PATHS[mode][PageMasterMode.DASHBOARD]} />} />
          </Route>
          <Route path="" element={<Navigate to={"/"} />} />
          <Route path="*" element={<Navigate to={"/"} />} />
        </MasterRouteContainer>
      );
    }

    // User is absolute expert
    const isGlobalExpert = isRoleGreaterThanOrEqual(user.role, ROLE.ORGANIZER);

    const renderMeetingMasterPages = () => {
      const defaultRoute = <Route path="*" element={<Navigate to=".." replace />} />;
      return (
        generateRoute(
          SessionMode.MASTER, 
          PageMasterMode.MEETING, 
          provideMeetingContext(provideMeetingConnector(
            <MeetingConnectorRenderer render={(state) => state.mode === "expert" ? <MeetingExpertPage /> : <MeetingStudentPage />} />
          )), 
          {
            children: (
              <>
                {generateRoute(
                  SessionMode.MASTER,
                  PageMasterMode.MEETING_DASHBOARD,
                  <MeetingConnectorRenderer render={(state) => (state.mode === "expert" ? <MeetingDashboardExpertPage /> : <MeetingDashboardStudentPage />)} />
                )}
                {generateRoute(
                  SessionMode.MASTER,
                  PageMasterMode.MEETING_CALL,
                  <MeetingConnectorRenderer render={(state) => (state.mode === "expert" ? <MeetingCallExpertPage /> : defaultRoute)} />
                )}
                {generateRoute(
                  SessionMode.MASTER,
                  PageMasterMode.MEETING_HIGHLIGHT,
                  <MeetingConnectorRenderer render={(state) => (state.mode === "expert" ? defaultRoute : <MeetingHighlightStudentPage />)} />
                )}
                {generateRoute(
                  SessionMode.MASTER,
                  PageMasterMode.MEETING_GROUP,
                  <MeetingConnectorRenderer render={(state) => (state.mode === "expert" ? <MeetingGroupExpertPage /> : <MeetingGroupStudentPage />)} />
                )}
                {generateRoute(
                  SessionMode.MASTER,
                  PageMasterMode.MEETING_SURVEY,
                  <MeetingConnectorRenderer render={(state) => (state.mode === "expert" ? <MeetingSurveyExpertPage /> : <MeetingSurveyStudentPage />)} />
                )}
                {generateRoute(
                  SessionMode.MASTER,
                  PageMasterMode.MEETING_PEDAGOGY,
                  <MeetingConnectorRenderer render={(state) => (state.mode === "expert" ? <MeetingExpertsWithPedagogyPage /> : <MeetingPedagogyStudentPage />)} />
                )}
              </>
            ),
          },
        )
      );
    }    

    if (isGlobalExpert) {
      return (
        <MasterRouteContainer mode="expert" element={provideMediaContext(provideMixedContexts(<PrivateRoute mode={mode} authStatus={authStatus} />))}>
          {generateRoute(SessionMode.MASTER, PageMasterMode.LOGOUT, <LogoutPage />)}
          {generateRoute(SessionMode.MASTER, PageMasterMode.LOGIN, <LoginRedirectionRoute defaultTo={PAGE_PATHS[mode][PageMasterMode.LIST]} />)}
          {generateRoute(SessionMode.MASTER, PageMasterMode.LIST, <MeetingListExpertPage />)}
          {renderMeetingMasterPages()}
          {generateRoute(SessionMode.MASTER, PageMasterMode.VOD, <VodExpertPage />)}
          <Route path="" element={<Navigate to={PAGE_PATHS[mode][PageMasterMode.LIST]} />} />
          <Route path="*" element={<Navigate to={PAGE_PATHS[mode][PageMasterMode.LIST]} />} />
        </MasterRouteContainer>
      );
    }

    return (
      <MasterRouteContainer mode="student" element={provideMediaContext(provideMixedContexts(<PrivateRoute mode={mode} authStatus={authStatus} />))}>
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOGOUT, <LogoutPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOGIN, <LoginRedirectionRoute defaultTo={PAGE_PATHS[mode][PageMasterMode.LIST]} />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.LIST, <MeetingListStudentPage />)}
        {renderMeetingMasterPages()}
        <Route path="" element={<Navigate to={PAGE_PATHS[mode][PageMasterMode.LIST]} />} />
        <Route path="*" element={<Navigate to={PAGE_PATHS[mode][PageMasterMode.LIST]} />} />
      </MasterRouteContainer>
    );
  };

  const publicRoutes = () => {
    if (!mode) return <Loading fullscreen />;
    if (mode === SessionMode.SLAVE || getGlobalQuery(url.href, NavigateGlobalQuery.SLAVE)) {
      return (
        <SlaveRouteContainer element={<PublicRoute mode={mode} authStatus={authStatus} />}>
          <Route path="" element={<PublicRedirectionRoute mode={mode} />} />
          <Route path="*" element={<PublicRedirectionRoute mode={mode} />} />
        </SlaveRouteContainer>
      );
    }
    return (
      <MasterRouteContainer mode="public" element={<PublicRoute mode={mode} authStatus={authStatus} />}>
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOGIN, <LoginPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.FORBIDDEN, <ForbiddenPage />)}
        <Route path="" element={<PublicRedirectionRoute mode={mode} />} />
        <Route path="*" element={<PublicRedirectionRoute mode={mode} />} />
      </MasterRouteContainer>
    );
  };

  const routes = () => {
    if (authStatus === AuthStatus.In) {
      if (user) return privateRoutes();
      return (
        <ConnexionView
          status={userFetchStatus}
          retry={() => {
            if (!user) {
              retry();
              return;
            }
          }}
        />
      );
    }
    return publicRoutes();
  };

  return <div className={"background"}>{routes()}</div>;
}

export default App;
